import { ShieldCheck, Users, Settings, Home as HomeIcon } from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "@/components/site/PageHero";
import FeatureStrip from "@/components/site/FeatureStrip";

const STEPS = [
  { n: 1, eyebrow: "Free Assessment", title: ["Schedule a Free", "Water Test"], img: "/img/tech-test.jpg", text: "Reach out online or in person and we'll schedule a convenient time to test your home's water. There's no cost and no obligation." },
  { n: 2, eyebrow: "Review Your Results", title: ["Understand", "Your Water"], img: "/img/results.jpg", text: "We'll review your water test results with you and explain what's in your water, how it affects your home and health, and which solutions make the most sense." },
  { n: 3, eyebrow: "Choose Your System", title: ["Get the Right", "Solution"], img: "/img/tank.jpg", text: "Based on your results and goals, we'll recommend the best water filtration system for your home, whether it's whole-home filtration, under sink reverse osmosis, or a combination." },
  { n: 4, eyebrow: "Purchase & Install", title: ["Receive Your Equipment", "and Get Installed"], img: "/img/tech-install.jpg", text: "Once you're ready, you can purchase your equipment directly through us. We'll connect you with our trusted local plumbing partners for professional installation, and they'll handle the rest. (Installation is optional.)" },
];

const TRUST = [
  { icon: ShieldCheck, title: "No Obligation", text: "Free water test with no pressure" },
  { icon: Users, title: "Local Experts", text: "Trusted installation partners in San Diego" },
  { icon: Settings, title: "Customized Solutions", text: "Systems designed for your home and water quality" },
  { icon: HomeIcon, title: "Cleaner, Healthier Homes", text: "Better tasting water for you and your family" },
];

const Step = ({ s, last }) => (
  <div data-testid={`hiw-step-${s.n}`} className="grid lg:grid-cols-2 gap-8 lg:gap-6 items-stretch">
    <div className="relative grid grid-cols-[48px_1fr] sm:grid-cols-[88px_1fr] gap-4 sm:gap-6 py-4">
      <div className="relative flex justify-center">
        <motion.span initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className="relative z-10 grid place-items-center h-10 w-10 rounded-full bg-[var(--ink)] text-white font-head font-bold">{s.n}</motion.span>
        {!last && <motion.span initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="absolute top-12 -bottom-10 w-px bg-[#D4D4D4] origin-top" />}
      </div>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8 }}>
        <p className="eyebrow !text-[11px]">{s.eyebrow}</p>
        <h2 className="font-head font-extrabold text-3xl sm:text-[2rem] leading-[1.08] mt-3">{s.title[0]}<br />{s.title[1]}</h2>
        <p className="mt-4 text-[17px] text-[var(--body)] leading-relaxed max-w-md">{s.text}</p>
      </motion.div>
    </div>
    <motion.div initial={{ clipPath: "inset(0 0 0 100%)" }} whileInView={{ clipPath: "inset(0 0 0 0%)" }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }} className="relative min-h-[240px] sm:min-h-[280px] overflow-hidden bg-[#EBEBEB]">
      <img src={s.img} alt={s.title.join(" ")} className="absolute inset-0 h-full w-full object-cover" />
    </motion.div>
  </div>
);

export default function HowItWorks() {
  return (
    <>
      <PageHero
        testId="hiw-hero"
        eyebrow="How It Works"
        lines={["A simpler way", "to cleaner,", "healthier water."]}
        text="We make it easy to get high quality water for your home. From a free assessment to the right equipment and optional installation through our trusted local plumbers, we guide you every step of the way."
        cta="Get Started"
        ctaTo="/signup"
        img="/img/tech-door.jpg"
        alt="Water System Bros. technician arriving at a San Diego home"
      />
      <section data-testid="hiw-steps" className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 space-y-10 lg:space-y-4">
          {STEPS.map((s, i) => <Step key={s.n} s={s} last={i === STEPS.length - 1} />)}
        </div>
      </section>
      <FeatureStrip items={TRUST} testId="hiw-trust-strip" className="bg-[var(--soft)]" />
    </>
  );
}
